/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  DynamicFormFieldConfig,
  findFieldFromConfig,
  QuestionnaireComponentKind,
  QuestionnaireFormComponentRegistry,
} from '@main/dynamic-form';

import {
  FormFieldCondition,
  PickFormFieldCondition,
  QuestionConditionChecker,
} from '../lib/conditions';

export function getQuestionConditionsFilter(
  config: DynamicFormFieldConfig<QuestionnaireFormComponentRegistry>,
  fields: readonly DynamicFormFieldConfig<QuestionnaireFormComponentRegistry>[],
  includeAncestorSections = false,
): QuestionConditionChecker {
  const checker = getConditionChecker(config.condition, fields, includeAncestorSections);
  if (!includeAncestorSections) {
    return checker;
  }

  const ancestorCheckers =
    findAncestorSections(fields, config.name)?.map((section) =>
      getConditionChecker(section.condition, fields, includeAncestorSections),
    ) ?? [];

  return (answers, answer) =>
    checker(answers, answer) || ancestorCheckers.some((checker) => checker(answers));
}

function findAncestorSections(
  fields: readonly DynamicFormFieldConfig<QuestionnaireFormComponentRegistry>[],
  fieldName: string,
): DynamicFormFieldConfig<QuestionnaireFormComponentRegistry>[] | undefined {
  for (const field of fields) {
    if (field.name === fieldName) {
      return [];
    }
    if (field.kind === QuestionnaireComponentKind.Section && field.fields) {
      const ancestors = findAncestorSections(field.fields, fieldName);
      if (ancestors) {
        return [field, ...ancestors];
      }
    }
  }
  return;
}

export function mapFieldCondition<TDeps extends any[]>(
  mapFn: (condition: FormFieldCondition, ...deps: TDeps) => FormFieldCondition,
  field: DynamicFormFieldConfig<QuestionnaireFormComponentRegistry>,
  ...deps: TDeps
): DynamicFormFieldConfig<QuestionnaireFormComponentRegistry> {
  return { ...field, condition: mapCondition(mapFn, field.condition, ...deps) };
}

export function mapFieldConditionPartial<TDeps extends any[]>(
  mapFn: (condition: FormFieldCondition, ...deps: TDeps) => FormFieldCondition,
) {
  return (field: DynamicFormFieldConfig<QuestionnaireFormComponentRegistry>, ...deps: TDeps) =>
    mapFieldCondition(mapFn, field, ...deps);
}

export function mapCondition<R extends FormFieldCondition, TDeps extends any[]>(
  mapFn: (condition: FormFieldCondition, ...deps: TDeps) => R,
  condition: FormFieldCondition,
  ...deps: TDeps
): R;
export function mapCondition<R extends FormFieldCondition, TDeps extends any[]>(
  mapFn: (condition: FormFieldCondition, ...deps: TDeps) => R,
  condition: FormFieldCondition | undefined,
  ...deps: TDeps
): R | undefined;
export function mapCondition<R extends FormFieldCondition, TDeps extends any[]>(
  mapFn: (condition: FormFieldCondition, ...deps: TDeps) => R,
  condition: FormFieldCondition | undefined,
  ...deps: TDeps
): R | undefined {
  if (!condition) {
    return;
  }

  if (condition.kind === 'group') {
    condition = {
      ...condition,
      conditions: condition.conditions.map((c) => mapCondition(mapFn, c, ...deps)),
    };
  }

  return mapFn(condition, ...deps);
}

function getConditionChecker(
  condition: FormFieldCondition | undefined,
  fields: readonly DynamicFormFieldConfig<QuestionnaireFormComponentRegistry>[],
  includeAncestorSections: boolean,
): QuestionConditionChecker {
  if (!condition) {
    return () => false;
  }

  const checker = conditionCheckers[condition.kind] as ConditionCheckerFactory | undefined;

  if (!checker) {
    // eslint-disable-next-line no-console
    console.warn(`Not implemented form question condition kind: ${condition.kind}`);
    return () => false;
  }

  return checker(condition, fields, includeAncestorSections);
}

type ConditionCheckerFactory<T extends FormFieldCondition = FormFieldCondition> = (
  condition: T,
  fields: readonly DynamicFormFieldConfig<QuestionnaireFormComponentRegistry>[],
  includeAncestorSections: boolean,
) => QuestionConditionChecker;
type ConditionCheckers = {
  [K in FormFieldCondition['kind']]: ConditionCheckerFactory<
    PickFormFieldCondition<FormFieldCondition, K>
  >;
};

const conditionCheckers: Partial<ConditionCheckers> = {
  group(condition, fields, includeAncestorSections) {
    const checkers = condition.conditions.map((condition) =>
      getConditionChecker(condition, fields, includeAncestorSections),
    );

    if (condition.logic === 'and') {
      return (answers, answer) => checkers.some((checker) => checker(answers, answer));
    } else {
      return (answers, answer) => checkers.every((checker) => checker(answers, answer));
    }
  },
  parent(condition, fields, includeAncestorSections) {
    return (answers) => {
      const parentAnswer = answers.find((a) => a.field_name === condition.fieldName);

      if (!parentAnswer) {
        return condition.byDefault === 'hide';
      }

      const matchChecker = Array.isArray(parentAnswer.value)
        ? (value: unknown) => (parentAnswer.value as unknown[]).includes(value)
        : (value: unknown) => parentAnswer.value === value;
      const isMatch = condition.values.some(matchChecker);
      const isHidden = condition.onMatch === 'show' ? !isMatch : isMatch;

      // Check conditionals chain if not hidden
      if (!isHidden) {
        const parentField = findFieldFromConfig(fields, condition.fieldName);
        const grandParentCondition =
          parentField && getQuestionConditionsFilter(parentField, fields, includeAncestorSections);

        return grandParentCondition?.(answers) ?? isHidden;
      }

      return isHidden;
    };
  },
};
